import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Calendar, TrendingUp, Activity, Utensils, Moon, Heart } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { UnifiedCalendarView } from '@/components/shared/UnifiedCalendarView';
import { format, subDays } from 'date-fns';

export default function HistoricalDataHub() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('calendar');

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border/50 bg-background/80 backdrop-blur-sm sticky top-0 z-10 shadow-sm">
        <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(-1)}
              className="hover:bg-primary/10"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl sm:text-3xl font-light">Historical Data Hub</h1>
              <p className="text-sm text-muted-foreground">
                Review your wellness journey across all tracking dimensions
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-1 md:grid-cols-3 lg:grid-cols-5 mb-6">
            <TabsTrigger value="calendar" className="gap-2">
              <Calendar className="h-4 w-4" />
              Calendar View
            </TabsTrigger>
            <TabsTrigger value="checkins" className="gap-2">
              <Heart className="h-4 w-4" />
              Check-Ins
            </TabsTrigger>
            <TabsTrigger value="nutrition" className="gap-2">
              <Utensils className="h-4 w-4" />
              Nutrition
            </TabsTrigger>
            <TabsTrigger value="activity" className="gap-2">
              <Activity className="h-4 w-4" />
              Activity
            </TabsTrigger>
            <TabsTrigger value="trends" className="gap-2">
              <TrendingUp className="h-4 w-4" />
              Trends
            </TabsTrigger>
          </TabsList>

          <TabsContent value="calendar" className="space-y-6">
            <UnifiedCalendarView />
            
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">How to Use the Calendar View</h3>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Click on any day with data to see all tracking details for that day</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Color dots indicate which data types were logged (blue=check-in, green=food, purple=WHOOP, red=menstrual)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Moon emojis show the lunar phase for each day</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-primary">•</span>
                  <span>Navigate months using the arrows or click "Today" to jump to current day</span>
                </li>
              </ul>
            </Card>
          </TabsContent>

          <TabsContent value="checkins">
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Daily Check-In History</h3>
              <p className="text-muted-foreground mb-4">
                Navigate to the Daily Check-In page to see your complete check-in history with date navigation.
              </p>
              <Button onClick={() => navigate('/daily-checkin')}>
                Go to Daily Check-Ins
              </Button>
            </Card>
          </TabsContent>

          <TabsContent value="nutrition">
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Nutrition History</h3>
              <p className="text-muted-foreground mb-4">
                Navigate to the Nutrition page to see your food log history and correlations.
              </p>
              <Button onClick={() => navigate('/nutrition')}>
                Go to Nutrition
              </Button>
            </Card>
          </TabsContent>

          <TabsContent value="activity">
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Activity & Recovery History</h3>
              <p className="text-muted-foreground mb-4">
                Navigate to the WHOOP Dashboard to see your activity, recovery, and sleep history.
              </p>
              <Button onClick={() => navigate('/whoop-dashboard')}>
                Go to WHOOP Dashboard
              </Button>
            </Card>
          </TabsContent>

          <TabsContent value="trends">
            <Card className="p-6">
              <h3 className="text-lg font-semibold mb-4">Wellness Trends</h3>
              <p className="text-muted-foreground mb-4">
                View comprehensive trend analysis across all your wellness metrics.
              </p>
              <Button onClick={() => navigate('/mind-body-insights')}>
                Go to Insights
              </Button>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
          <Card className="p-4">
            <div className="flex items-center gap-3 mb-2">
              <Heart className="h-5 w-5 text-primary" />
              <h4 className="font-semibold">Check-In Consistency</h4>
            </div>
            <p className="text-2xl font-bold">Track your daily wellness</p>
            <p className="text-xs text-muted-foreground mt-1">
              Click on any past day to view or edit your check-in
            </p>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3 mb-2">
              <TrendingUp className="h-5 w-5 text-green-500" />
              <h4 className="font-semibold">Pattern Recognition</h4>
            </div>
            <p className="text-2xl font-bold">Spot correlations</p>
            <p className="text-xs text-muted-foreground mt-1">
              Click on individual metrics to see detailed history charts
            </p>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3 mb-2">
              <Moon className="h-5 w-5 text-purple-500" />
              <h4 className="font-semibold">Lunar Tracking</h4>
            </div>
            <p className="text-2xl font-bold">Connect rhythms</p>
            <p className="text-xs text-muted-foreground mt-1">
              See how lunar phases correlate with your wellness
            </p>
          </Card>
        </div>
      </div>
    </div>
  );
}
